import { Box, BoxProps, styled } from "@mui/material";
import { FC } from "react";

import {
  availableStreetlightStatusColors,
  isStreetlightStatusType,
  STREETLIGHT_STATUS_TYPE,
} from "../../types/streetlight-status-type";

type SizeType = "normal" | "large";

export interface StreetlightStatusIndicatorProps {
  /** Size of status indicators */
  size?: SizeType;
  /** Active status */
  status: STREETLIGHT_STATUS_TYPE | string | undefined;
  /** The position of the traffic light indicator. Only to be used in conjuction with a "string" as the status. Should be either 1, 2 or 3 three! */
  position?: number;
  /** (optional) Color for the circle borders */
  borderColor?: string;
  /** (optional) Color for the non-active circle background */
  defaultBackgroundColor?: string;
  /** Id used for automated testing */
  "data-testid"?: string;
  /** further styling */
  cardProps?: BoxProps;
}

const isLarge = (size: SizeType) => size === "large";
const Status = styled("span")<{ size: SizeType }>(({ size }) => ({
  width: isLarge(size) ? 20 : 15,
  height: isLarge(size) ? 20 : 15,
  borderRadius: "50%",
  display: "inline-block",
  marginLeft: isLarge(size) ? 6 : 4,
}));

/** Traffic light style status indicator */
export const StreetlightStatusIndicator: FC<
  StreetlightStatusIndicatorProps
> = ({
  status,
  size = "normal",
  "data-testid": dataTestId,
  borderColor,
  defaultBackgroundColor = "white",
  cardProps = {},
  position,
}) => {
  let pos = position;
  if (pos && pos < 1) {
    pos = 1;
  }
  if (pos && pos > 3) {
    pos = 3;
  }
  const positionOfIndexForStatus = status
    ? Object.keys(availableStreetlightStatusColors).indexOf(status, 0)
    : -1;
  return (
    <Box
      data-testid={dataTestId || "StatusIndicator"}
      sx={{ display: "inline-flex" }}
      aria-label={status}
    >
      {Object.values(availableStreetlightStatusColors).map((_, index) => (
        <Status
          key={index}
          size={size}
          sx={{
            borderColor: borderColor,
            borderWidth: borderColor ? "1px" : undefined,
            borderStyle: borderColor ? "solid" : undefined,
            ...cardProps.sx,
            backgroundColor: !status
              ? "#fff"
              : positionOfIndexForStatus === index || pos === index
                ? isStreetlightStatusType(status)
                  ? availableStreetlightStatusColors[status]
                  : status
                : defaultBackgroundColor,
          }}
        />
      ))}
    </Box>
  );
};
